import _ from 'lodash';

import { getEnvironment } from './environment';
import { getUrlParam, getUrls } from './url';

/**
 * A state that is preserved through authentication requests,
 * OAuth or otherwise, and delivered to an oauth state handler
 * (i.e. v1_index_client or /auth, see "target")
 */
export interface OauthState {
    account?: string;
    app_breakout?: any;
    app_mode?: any;
    autojoin?: boolean;
    external?: boolean;
    external_login?: boolean;
    lang?: string;
    native?: boolean;
    native_platform?: string;
    native_version?: string;
    rand?: number;
    /**
     * The navigation location after a successful authentication
     * has taken place. A user should land on this page or path
     * after auth is complete.
     */
    redirect?: string;
    /**
     * If true, the redirect path will be as is defined in the "redirect"
     */
    redirect_absolute?: boolean;
    school_name?: string;
    secondary_stage?: string;
    showExperimentalSettings?: boolean;
    showOrgType?: boolean;
    source?: string;
    stage?: string;
    /**
     * The path for redirect after OAuth takes place. This path
     * will receive the state parameter from OAuth.
     */
    target?: string;
    /**
     * The token that will be associated with user information
     * after authentication.
     */
    user_uuid?: string;
    websdk?: boolean;
}

export const defaultOauthState = () => {
    const { account, environment } = getEnvironment();
    const { vanityUrl } = getUrls();

    return {
        websdk: true,
        stage: environment,
        target: 'auth',
        school_name: account,
        redirect: `${vanityUrl}/v1_index_client.html`
    };
};

export const createOauthState = (state: Partial<OauthState> = {}): OauthState => _.merge(defaultOauthState(), state);

export const parseOauthState = (): OauthState | undefined => {
    const state = getUrlParam('state');

    if (!state) {
        return undefined;
    }

    let params: OauthState = {
        target: 'auth' // default react auth handler for oauth responses
    };

    try {
        params = JSON.parse(state);
    } catch (error) {
        try {
            params = JSON.parse(decodeURI(state));
        } catch (err) {
            /**
             * Some remnants of the "legacy" state format may still
             * exist, i.e. name:value/name:value/ etc.
             */
            state.split('/').forEach((stateParam) => {
                const [name, value] = stateParam.split(':');
                params[name] = value;
            });
        }
    }

    return params;
};
